import { Trash2 } from 'lucide-react';

import { errorMessage } from '../../../lib/errorMessage';
import { Drawer } from '../../../components/Drawer/Drawer';
import { useToast } from '../../../components/Toast';
import { TaskForm } from './TaskForm';
import type { TaskInput, TaskProjectOption, TaskRow } from './tasks.schema';
import { useSoftDeleteTask, useUpsertTask } from './tasksQueries';
import styles from './tasks.module.css';

const FORM_ID = 'task-form';

interface TaskDrawerProps {
  open: boolean;
  task: TaskRow | null;
  projects: TaskProjectOption[];
  defaultProjectId?: string;
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
}

export function TaskDrawer({ open, task, projects, defaultProjectId, orgId, userId, onClose }: TaskDrawerProps) {
  const toast = useToast();
  const upsertTask = useUpsertTask(orgId, userId);
  const softDeleteTask = useSoftDeleteTask(orgId);
  const isEditing = Boolean(task);
  const busy = upsertTask.isPending || softDeleteTask.isPending;

  async function handleSubmit(values: TaskInput) {
    try {
      await upsertTask.mutateAsync({ id: task?.id ?? null, values });
      toast.success('המשימה נשמרה');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  async function handleDelete() {
    if (!task) return;
    if (!window.confirm(`למחוק את "${task.title}"?`)) return;

    try {
      await softDeleteTask.mutateAsync(task.id);
      toast.success('המשימה נמחקה');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="משימה"
      title={isEditing ? task?.title : 'משימה חדשה'}
      ariaLabel={isEditing ? 'עריכת משימה' : 'משימה חדשה'}
      footer={
        <>
          {isEditing ? (
            <button
              type="button"
              className={styles.btnDanger}
              disabled={busy}
              onClick={() => {
                void handleDelete();
              }}
            >
              <Trash2 size={15} aria-hidden="true" />
              מחק
            </button>
          ) : null}
          <button type="button" className={styles.btnSecondary} disabled={busy} onClick={onClose}>
            בטל
          </button>
          <button type="submit" form={FORM_ID} className={styles.btnPrimary} disabled={busy}>
            שמור
          </button>
        </>
      }
    >
      <TaskForm
        task={task}
        projects={projects}
        {...(defaultProjectId ? { defaultProjectId } : {})}
        formId={FORM_ID}
        disabled={busy}
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
}
